import { CommonModule, isPlatformBrowser } from "@angular/common";
import {
  booleanAttribute,
  Component,
  computed,
  inject,
  Input,
  model,
  OnInit,
  PLATFORM_ID,
  Signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { $t } from "@primeng/themes";
import { PrimeNG } from "primeng/config";
import { DrawerModule } from "primeng/drawer";
import { RadioButtonModule } from "primeng/radiobutton";
import { SelectButtonModule } from "primeng/selectbutton";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { BRAND_CONFIG } from "../../../branding/brand-config";
import { LayoutService } from "../service/layout.service";

@Component({
  selector: "app-configurator",
  standalone: true,
  imports: [CommonModule, FormsModule, SelectButtonModule, DrawerModule, ToggleSwitchModule, RadioButtonModule],
  template: `
    <button *ngIf="simple" class="layout-config-button config-link" type="button" (click)="toggleConfigSidebar()">
      <i class="pi pi-cog"></i>
    </button>
    <p-drawer
      [visible]="visible()"
      (onHide)="onDrawerHide()"
      position="right"
      [transitionOptions]="'.3s cubic-bezier(0, 0, 0.2, 1)'"
      styleClass="layout-config-sidebar w-80"
      header="Settings"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-lg font-semibold">Color Scheme</span>
          <p-selectbutton
            [ngModel]="darkTheme()"
            (ngModelChange)="toggleDarkMode()"
            [options]="themeOptions"
            optionLabel="name"
            optionValue="value"
            [allowEmpty]="false"
          ></p-selectbutton>
        </div>

        <div *ngIf="!simple" class="flex flex-col gap-2">
          <span class="text-lg font-semibold">Menu Type</span>
          <div class="flex flex-wrap flex-col gap-3">
            <div class="flex">
              <div class="flex items-center gap-2 w-1/2">
                <p-radio-button
                  name="menuMode"
                  value="static"
                  [(ngModel)]="menuMode"
                  (ngModelChange)="setMenuMode('static')"
                  inputId="static"
                ></p-radio-button>
                <label for="static">Static</label>
              </div>

              <div class="flex items-center gap-2 w-1/2">
                <p-radio-button
                  name="menuMode"
                  value="overlay"
                  [(ngModel)]="menuMode"
                  (ngModelChange)="setMenuMode('overlay')"
                  inputId="overlay"
                ></p-radio-button>
                <label for="overlay">Overlay</label>
              </div>
            </div>
            <div class="flex">
              <div class="flex items-center gap-2 w-1/2">
                <p-radio-button
                  name="menuMode"
                  value="slim"
                  [(ngModel)]="menuMode"
                  (ngModelChange)="setMenuMode('slim')"
                  inputId="slim"
                ></p-radio-button>
                <label for="slim">Slim</label>
              </div>
              <div class="flex items-center gap-2 w-1/2">
                <p-radio-button
                  name="menuMode"
                  value="slim-plus"
                  [(ngModel)]="menuMode"
                  (ngModelChange)="setMenuMode('slim-plus')"
                  inputId="slim-plus"
                ></p-radio-button>
                <label for="slim-plus">Slim+</label>
              </div>
            </div>
            <div class="flex">
              <div class="flex items-center gap-2 w-1/2">
                <p-radio-button
                  name="menuMode"
                  value="reveal"
                  [(ngModel)]="menuMode"
                  (ngModelChange)="setMenuMode('reveal')"
                  inputId="reveal"
                ></p-radio-button>
                <label for="reveal">Reveal</label>
              </div>
              <div class="flex items-center gap-2 w-1/2">
                <p-radio-button
                  name="menuMode"
                  value="drawer"
                  [(ngModel)]="menuMode"
                  (ngModelChange)="setMenuMode('drawer')"
                  inputId="drawer"
                ></p-radio-button>
                <label for="drawer">Drawer</label>
              </div>
            </div>
            <div class="flex items-center gap-2 w-1/2">
              <p-radio-button
                name="menuMode"
                value="horizontal"
                [(ngModel)]="menuMode"
                (ngModelChange)="setMenuMode('horizontal')"
                inputId="horizontal"
              ></p-radio-button>
              <label for="horizontal">Horizontal</label>
            </div>
          </div>
        </div>

        <div *ngIf="!simple" class="flex flex-col gap-2">
          <span class="text-lg font-semibold">Menu Theme</span>
          <div class="flex flex-wrap flex-col gap-4">
            <div class="flex items-center gap-2">
              <p-radiobutton
                name="menuTheme"
                value="colorScheme"
                [ngModel]="menuTheme()"
                (ngModelChange)="setMenuTheme('colorScheme')"
                inputId="menutheme-colorscheme"
              ></p-radiobutton>
              <label for="scheme">Color Scheme</label>
            </div>

            <div class="flex items-center gap-2">
              <p-radiobutton
                name="menuTheme"
                value="primaryColor"
                [ngModel]="menuTheme()"
                (ngModelChange)="setMenuTheme('primaryColor')"
                inputId="menutheme-primarycolor"
              ></p-radiobutton>
              <label for="primary">Primary Color</label>
            </div>
            <div class="flex items-center gap-2">
              <p-radiobutton
                name="menuTheme"
                value="transparent"
                [ngModel]="menuTheme()"
                (ngModelChange)="setMenuTheme('transparent')"
                inputId="menutheme-transparent"
                [disabled]="isTransparentThemeOptionDisabled()"
              ></p-radiobutton>
              <label for="transparent">Transparent</label>
            </div>
          </div>
        </div>
      </div>
    </p-drawer>
  `,
})
export class AppConfiguratorComponent implements OnInit {
  @Input({ transform: booleanAttribute }) simple = false;

  private readonly brandConfig = inject(BRAND_CONFIG);

  config: PrimeNG = inject(PrimeNG);

  layoutService: LayoutService = inject(LayoutService);

  platformId = inject(PLATFORM_ID);

  themeOptions = [
    { name: "Light", value: false },
    { name: "Dark", value: true },
  ];

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Apply the brand's preset, primary palette, and surface palette at init.
      // The primary palette is extracted from the preset's primitive section (keyed by brandId)
      // so PrimeNG exposes the brand's colors as --p-primary-50..950 CSS variables.
      const primaryPalette = this.brandConfig.primeNgPreset.primitive?.[this.brandConfig.brandId];
      if (!primaryPalette) {
        throw new Error(
          `Brand "${this.brandConfig.brandId}" is misconfigured: ` +
            `its PrimeNG preset must define a primitive palette under the key "${this.brandConfig.brandId}" ` +
            `(e.g. primitive: { ${this.brandConfig.brandId}: { 50: "#...", ..., 950: "#..." } }).`,
        );
      }
      $t()
        .preset(this.brandConfig.primeNgPreset)
        .primaryPalette(primaryPalette)
        .surfacePalette(this.brandConfig.surfacePalette)
        .use({ useDefaultOptions: true });
    }
  }

  menuMode = model(this.layoutService.layoutConfig().menuMode);

  visible: Signal<boolean> = computed(() => this.layoutService.layoutState().configSidebarVisible);

  darkTheme = computed(() => this.layoutService.layoutConfig().darkTheme);

  menuTheme = computed(() => this.layoutService.layoutConfig().menuTheme);

  onDrawerHide() {
    this.layoutService.hideConfigSidebar();
  }

  setMenuMode(mode: string) {
    const nonTransparentModes = ["reveal", "drawer", "overlay"];
    const currentMenuTheme = this.menuTheme();

    if (nonTransparentModes.includes(mode)) {
      const theme =
        currentMenuTheme === "colorScheme" || currentMenuTheme === "primaryColor" ? currentMenuTheme : "colorScheme";

      this.setMenuTheme(theme);
    }
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      menuMode: mode,
    }));

    if (this.layoutService.layoutConfig().menuMode === "static") {
      this.layoutService.layoutState.update((state) => ({
        ...state,
        staticMenuDesktopInactive: false,
      }));
    }
  }

  toggleDarkMode() {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      darkTheme: !state.darkTheme,
    }));
  }

  toggleConfigSidebar() {
    this.layoutService.layoutState.update((val) => ({ ...val, configSidebarVisible: !val.configSidebarVisible }));
  }

  setMenuTheme(theme: string) {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      menuTheme: theme,
    }));
  }

  isTransparentThemeOptionDisabled() {
    const menuMode = this.menuMode();
    return ["reveal", "overlay", "drawer"].includes(menuMode as string);
  }
}
